import React, { useMemo, useCallback, useRef, useEffect, useState } from 'react';
import { createEditor, Editor, Transforms, Range, Element as SlateElement } from 'slate';
import { Slate, Editable, withReact } from 'slate-react';
import { withHistory } from 'slate-history';
import isHotkey from 'is-hotkey';
import { SlateToolbar, toggleMark } from './SlateToolbar';
import { serializeToMarkdown } from './serializer';
import { deserializeMarkdown } from './deserializer';
import './SlateEditor.css';

const EMPTY_DOCUMENT = [{ type: 'paragraph', children: [{ text: '' }] }];

const HOTKEYS = {
  'mod+b': 'bold',
  'mod+i': 'italic',
  'mod+`': 'code',
};

const INLINE_TYPES = ['link'];
const VOID_TYPES = ['image'];

/** 简单启发式：粘贴的纯文本是否像 Markdown（用于决定是否用 deserializeMarkdown 解析） */
const looksLikeMarkdown = (text) => {
  return true;
  if (!text || typeof text !== 'string') return false;
  const t = text.trim();
  if (!t) return false;
  return (
    /^#+\s/m.test(t) ||
    /\*\*[^*]+\*\*|\*\s[^*]*\*/.test(t) ||
    /^-\s|^\*\s|^\d+\.\s/m.test(t) ||
    /^>\s/m.test(t) ||
    /^---\s*$/m.test(t) ||
    /`[^`]+`/.test(t)
  );
};

/** Plugin: make links inline elements */
const withInlines = (editor) => {
  const { isInline, isVoid } = editor;
  editor.isInline = (element) =>
    INLINE_TYPES.includes(element.type) ? true : isInline(element);
  editor.isVoid = (element) =>
    VOID_TYPES.includes(element.type) ? true : isVoid(element);
  return editor;
};

/** Plugin: prevent normalizing from breaking table structure */
const withTables = (editor) => {
  const { deleteBackward, deleteForward, insertBreak } = editor;

  editor.deleteBackward = (unit) => {
    const { selection } = editor;
    if (selection && Range.isCollapsed(selection)) {
      const [cell] = Editor.nodes(editor, {
        match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === 'table-cell',
      });
      if (cell) {
        const [, cellPath] = cell;
        if (Editor.isStart(editor, selection.anchor, cellPath)) {
          return;
        }
      }
    }
    deleteBackward(unit);
  };

  editor.deleteForward = (unit) => {
    const { selection } = editor;
    if (selection && Range.isCollapsed(selection)) {
      const [cell] = Editor.nodes(editor, {
        match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === 'table-cell',
      });
      if (cell) {
        const [, cellPath] = cell;
        if (Editor.isEnd(editor, selection.anchor, cellPath)) {
          return;
        }
      }
    }
    deleteForward(unit);
  };

  editor.insertBreak = () => {
    const { selection } = editor;
    if (selection) {
      const [table] = Editor.nodes(editor, {
        match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === 'table',
      });
      if (table) {
        return;
      }
    }
    insertBreak();
  };

  return editor;
};

/** Link element renderer */
const LinkElement = ({ attributes, children, element }) => (
  <a {...attributes} href={element.url} title={element.url} className="slate-link">
    {children}
  </a>
);

/** Image element renderer (void) */
const ImageElement = ({ attributes, children, element }) => (
  <div {...attributes} className="slate-image-wrapper">
    {children}
    <div contentEditable={false}>
      <img src={element.url} alt={element.alt || ''} className="slate-image" />
    </div>
  </div>
);

/** Table element renderers */
const TableElement = ({ attributes, children }) => (
  <table className="slate-table" {...attributes}><tbody>{children}</tbody></table>
);
const TableRowElement = ({ attributes, children }) => (
  <tr {...attributes}>{children}</tr>
);
const TableCellElement = ({ attributes, children }) => (
  <td {...attributes}>{children}</td>
);

const SlateEditor = ({ value, onChange, placeholder, disabled, height = 300, style, initialMarkdown }) => {
  const editor = useMemo(() => withTables(withInlines(withHistory(withReact(createEditor())))), []);
  const isResettingRef = useRef(false);
  const prevValueRef = useRef(value);
  const [key, setKey] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Compute initial Slate value from markdown (only on mount)
  const [computedInitialValue] = useState(() => {
    if (initialMarkdown && typeof initialMarkdown === 'string' && initialMarkdown.trim()) {
      return deserializeMarkdown(initialMarkdown);
    }
    return EMPTY_DOCUMENT.map(n => ({ ...n, children: n.children.map(c => ({ ...c })) }));
  });

  // Detect external reset (value goes to empty string)
  useEffect(() => {
    if ((value === '' || value === undefined || value === null) && prevValueRef.current !== value) {
      isResettingRef.current = true;
      setKey((k) => k + 1);
      editor.children = [...EMPTY_DOCUMENT.map((n) => ({ ...n, children: n.children.map((c) => ({ ...c })) }))];
      try {
        const point = Editor.start(editor, []);
        Transforms.select(editor, point);
      } catch (e) {
        // ignore if editor not ready
      }
    }
    prevValueRef.current = value;
  }, [value, editor]);

  // Lock body scroll when fullscreen
  useEffect(() => {
    if (isFullscreen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isFullscreen]);

  // Escape key to exit fullscreen
  useEffect(() => {
    if (!isFullscreen) return;
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        setIsFullscreen(false);
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isFullscreen]);

  const handleChange = useCallback(
    (newValue) => {
      if (isResettingRef.current) {
        isResettingRef.current = false;
        return;
      }

      const isAstChange = editor.operations.some((op) => op.type !== 'set_selection');
      if (isAstChange && onChange) {
        const markdown = serializeToMarkdown(newValue);
        onChange(markdown);
      }
    },
    [editor, onChange]
  );

  const renderElement = useCallback((props) => {
    const { attributes, children, element } = props;
    switch (element.type) {
      case 'heading-one':
        return <h1 {...attributes}>{children}</h1>;
      case 'heading-two':
        return <h2 {...attributes}>{children}</h2>;
      case 'block-quote':
        return <blockquote {...attributes}>{children}</blockquote>;
      case 'bulleted-list':
        return <ul {...attributes}>{children}</ul>;
      case 'numbered-list':
        return <ol {...attributes}>{children}</ol>;
      case 'list-item':
        return <li {...attributes}>{children}</li>;
      case 'link':
        return <LinkElement {...props} />;
      case 'image':
        return <ImageElement {...props} />;
      case 'table':
        return <TableElement {...props} />;
      case 'table-row':
        return <TableRowElement {...props} />;
      case 'table-cell':
        return <TableCellElement {...props} />;
      default:
        return <p {...attributes}>{children}</p>;
    }
  }, []);

  const renderLeaf = useCallback(({ attributes, children, leaf }) => {
    if (leaf.bold) children = <strong>{children}</strong>;
    if (leaf.italic) children = <em>{children}</em>;
    if (leaf.code) children = <code>{children}</code>;
    return <span {...attributes}>{children}</span>;
  }, []);

  const onKeyDown = useCallback(
    (event) => {
      if (event.key === 'Tab') {
        const [table] = Editor.nodes(editor, {
          match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === 'table',
        });
        if (table) {
          event.preventDefault();
          const [cell] = Editor.nodes(editor, {
            match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === 'table-cell',
          });
          if (cell) {
            const [, cellPath] = cell;
            try {
              const nextPath = event.shiftKey
                ? Editor.before(editor, cellPath, { unit: 'block' })
                : Editor.after(editor, cellPath, { unit: 'block' });
              if (nextPath) {
                Transforms.select(editor, nextPath);
              }
            } catch (e) {
              // at boundary
            }
          }
          return;
        }
      }

      for (const hotkey in HOTKEYS) {
        if (isHotkey(hotkey, event)) {
          event.preventDefault();
          toggleMark(editor, HOTKEYS[hotkey]);
        }
      }

      if (isHotkey('mod+k', event)) {
        event.preventDefault();
        window.dispatchEvent(new CustomEvent('slate-insert-link'));
      }
    },
    [editor]
  );

  /** 粘贴时若为 Markdown 文本则解析为富文本再插入，从而按标题/加粗等渲染 */
  const onPaste = useCallback(
    (event) => {
      const text = event.clipboardData?.getData?.('text/plain');
      if (!text || !looksLikeMarkdown(text)) return;

      event.preventDefault();
      const { selection } = editor;
      if (!selection) return;

      try {
        const nodes = deserializeMarkdown(text);
        if (!nodes || nodes.length === 0) return;

        const insertIndex = selection.anchor.path[0];
        Transforms.delete(editor, { at: selection });
        Transforms.insertNodes(editor, nodes, { at: [insertIndex] });

        const lastBlockPath = [insertIndex + nodes.length - 1];
        const endPoint = Editor.end(editor, lastBlockPath);
        Transforms.select(editor, endPoint);
      } catch (err) {
        console.warn('[SlateEditor] Paste as markdown parse failed, fallback to plain text:', err);
      }
    },
    [editor]
  );

  return (
    <>
      {/* Backdrop when fullscreen */}
      {isFullscreen && <div className="slate-fullscreen-backdrop" onClick={() => setIsFullscreen(false)} />}

      {/* Single editor container - always mounted, CSS switches between inline and fullscreen */}
      <div
        className={`slate-editor-container${isFullscreen ? ' slate-fullscreen-active' : ''}${disabled ? ' disabled' : ''}`}
        style={isFullscreen ? undefined : { height, minHeight: height, maxHeight: height, ...style }}
      >
        <Slate key={key} editor={editor} initialValue={computedInitialValue} onChange={handleChange}>
          <SlateToolbar
            isFullscreen={isFullscreen}
            onToggleFullscreen={() => setIsFullscreen((f) => !f)}
          />
          <Editable
            className="slate-editable-area"
            renderElement={renderElement}
            renderLeaf={renderLeaf}
            placeholder={placeholder}
            onKeyDown={onKeyDown}
            onPaste={onPaste}
            spellCheck={false}
            readOnly={disabled}
          />
        </Slate>
      </div>
    </>
  );
};

export default SlateEditor;
